import {Component, Inject, LOCALE_ID, OnInit} from '@angular/core';
import {TenantService} from '@twpub/core/services';
import {TenantConfiguration} from '@twpub/core/models/tenant-configuration';
import {MatCheckbox} from '@angular/material/checkbox';
import {MatExpansionModule} from '@angular/material/expansion';
import {NGXLogger} from 'ngx-logger';
import {AVAILABLE_UI_LANGUAGES, UiLanguageOption} from '@twpub/core/models/ui-language';
import {environment} from '../../../../environments/environment';

@Component({
  selector: 'pub-localization-settings',
  templateUrl: './localization-settings.component.html',
  styleUrls: ['./localization-settings.component.scss'],
  standalone: true,
  imports: [MatExpansionModule, MatCheckbox]
})
export class LocalizationSettingsComponent implements OnInit {
  availableLanguages = AVAILABLE_UI_LANGUAGES;
  enabledLanguages: Set<string> = new Set();
  private tenantConfiguration?: TenantConfiguration;
  private defaultLocale: string;

  constructor(private logger: NGXLogger, private tenantService: TenantService, @Inject(LOCALE_ID) localeId: string) {
    this.defaultLocale = localeId.split('-')[0];
  }

  ngOnInit(): void {
    this.tenantService.getCurrentTenant().subscribe({
      next: (config) => {
        this.tenantConfiguration = config;
        const languages = config.enabledUiLanguages ?? [];
        this.enabledLanguages = new Set(languages.length > 0 ? languages : [this.defaultLocale]);
      }
    });
  }

  isEnabled(code: string): boolean {
    return this.enabledLanguages.has(code);
  }

  isDisabled(code: string): boolean {
    return this.enabledLanguages.size <= 1 && this.enabledLanguages.has(code);
  }

  onToggle(code: string, checked: boolean): void {
    if (checked) {
      this.enabledLanguages.add(code);
    } else {
      if (this.enabledLanguages.size <= 1) return;
      this.enabledLanguages.delete(code);
    }
    this.save();
  }

  private save(): void {
    if (this.tenantConfiguration) {
      this.tenantConfiguration.enabledUiLanguages = [...this.enabledLanguages];
      this.tenantService.updateConfiguration(this.tenantConfiguration).subscribe({
        next: () => {
          this.logger.debug('Localization settings saved');
          if (environment.production && !this.enabledLanguages.has(this.defaultLocale)) {
            // Current locale was removed — redirect to the first enabled language
            const target = [...this.enabledLanguages][0];
            const path = window.location.pathname;
            const hash = window.location.hash;
            const localePattern = /^\/(en|hu|sv|de)(\/|$)/;
            let newPath = localePattern.test(path)
              ? path.replace(localePattern, `/${target}$2`)
              : `/${target}${path}`;
            window.location.replace(newPath + hash);
          }
        },
        error: (err) => this.logger.error('Failed to save localization settings', err)
      });
    }
  }
}
